import React, { useEffect, useRef, useState } from "react";
import { SignalBars } from "./common";
import { Lock, Radio, Activity, Server, Gauge, ShieldAlert } from "lucide-react";

const THREAT = {
  NOMINAL: { c: "#22C55E", l: "NOMINAL" },
  ELEVE: { c: "var(--r-secondary)", l: "ÉLEVÉ" },
  CRITIQUE: { c: "var(--r-alert)", l: "CRITIQUE" },
};

const LOG_POOL = [
  "handshake TLS 1.3 renégocié",
  "paquet chiffré ICE-9 acheminé",
  "balayage des fréquences adverses",
  "empreinte réseau brouillée",
  "clé RSA-4096 pivotée",
  "routage onion recalculé",
  "tunnel quantique scellé",
  "cache de session purgé",
  "sonde passive neutralisée",
  "intégrité du maillage vérifiée",
  "relais tachyonique resynchronisé",
  "trafic fantôme injecté",
  "certificat porteur revalidé",
  "latence inter-nœud stabilisée",
];

const GENERIC_TICKER = [
  "RÉSEAU RÉPLA OPÉRATIONNEL",
  "CHIFFREMENT ICE-9 ACTIF SUR TOUTES LES TRANSMISSIONS",
  "AUCUNE INTERCEPTION DÉTECTÉE SUR LE MAILLAGE",
  "7 RELAIS ONION EN LIGNE · ROUTAGE NOMINAL",
  "RAPPEL : NE DIVULGUEZ JAMAIS VOTRE ID RÉPLA PRIMAIRE",
  "SYNCHRONISATION TACHYONIQUE STABLE",
];

export function buildTicker(hud, feeds, networkName) {
  const items = [];
  (hud?.ticker || []).forEach((t) => t && items.push(String(t)));
  (feeds || []).forEach((f) => {
    if (f.last_message?.content) items.push(`${(f.name || "FLUX").toUpperCase()} · ${f.last_message.content}`);
  });
  if (networkName) items.push(`${networkName} · CANAL SÉCURISÉ`);
  if (items.length < 3) GENERIC_TICKER.forEach((g) => items.push(g));
  return items;
}

function prefersReduced() {
  return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function useHudMetrics() {
  const [m, setM] = useState({
    ping: 42, sync: 99.2, integrity: 98, relays: 7,
    activity: Array.from({ length: 30 }, () => 30 + Math.random() * 40),
  });
  useEffect(() => {
    if (prefersReduced()) return;
    const id = setInterval(() => {
      setM((p) => ({
        ping: clamp(p.ping + (Math.random() * 16 - 8), 18, 96),
        sync: clamp(p.sync + (Math.random() * 0.4 - 0.2), 96.5, 99.9),
        integrity: clamp(p.integrity + (Math.random() * 2 - 1), 92, 100),
        relays: Math.random() < 0.05 ? 6 + Math.floor(Math.random() * 3) : p.relays,
        activity: [...p.activity.slice(1), clamp(p.activity[p.activity.length - 1] + (Math.random() * 30 - 15), 8, 80)],
      }));
    }, 1600);
    return () => clearInterval(id);
  }, []);
  return m;
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  return now;
}

function useUptime() {
  const [s, setS] = useState(0);
  useEffect(() => { const id = setInterval(() => setS((x) => x + 1), 1000); return () => clearInterval(id); }, []);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(Math.floor(s / 3600))}:${p(Math.floor(s / 60) % 60)}:${p(s % 60)}`;
}

function stamp(now, hud) {
  const p = (n) => String(n).padStart(2, "0");
  const label = hud?.time_label || "RT";
  let date = hud?.fictional_date;
  if (!date) {
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 0));
    const day = Math.floor((now - start) / 86400000);
    date = `${now.getUTCFullYear() + 62}.${String(day).padStart(3, "0")}`;
  }
  return { date, time: `${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())} ${label}` };
}

function Cell({ label, children }) {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      {label ? <span className="font-mono tracking-[0.15em]" style={{ fontSize: 9, color: "var(--r-text-muted)" }}>{label}</span> : null}
      <span className="font-mono text-[11px]" style={{ color: "var(--r-text)" }}>{children}</span>
    </div>
  );
}

export function HudStatusBar({ hud }) {
  const m = useHudMetrics();
  const now = useClock();
  const up = useUptime();
  const s = stamp(now, hud);
  const threat = THREAT[hud?.threat_level] || THREAT.NOMINAL;
  return (
    <div data-testid="hud-status-bar" className="flex items-center gap-4 px-3 h-7 overflow-x-auto no-scrollbar border-b border-[color:var(--r-primary)]/12" style={{ background: "color-mix(in srgb, var(--r-panel) 92%, transparent)" }}>
      <Cell label="DATE"><span style={{ color: "var(--r-primary)" }}>{s.date}</span></Cell>
      <Cell>{s.time}</Cell>
      <div className="flex items-center gap-1.5 shrink-0"><span className="font-mono tracking-[0.15em]" style={{ fontSize: 9, color: "var(--r-text-muted)" }}>SIG</span><SignalBars /></div>
      <Cell label="ICE-9"><span className="inline-flex items-center gap-1"><Lock size={10} style={{ color: "var(--r-primary)" }} /><span style={{ color: "#22C55E" }}>ACTIF</span></span></Cell>
      <Cell label="RELAIS">{m.relays}×</Cell>
      <Cell label="PING">{Math.round(m.ping)}ms</Cell>
      <Cell label="SYNC">{m.sync.toFixed(1)}%</Cell>
      <Cell label="UP">{up}</Cell>
      <div className="flex items-center gap-1.5 shrink-0 ml-auto pl-3">
        <span className="font-mono tracking-[0.15em]" style={{ fontSize: 9, color: "var(--r-text-muted)" }}>MENACE</span>
        <span data-testid="hud-threat" className="font-mono text-[10px] px-1.5 py-0.5 rounded inline-flex items-center" style={{ color: threat.c, border: `1px solid ${threat.c}`, background: `color-mix(in srgb, ${threat.c} 12%, transparent)` }}>
          <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: threat.c, marginRight: 5, boxShadow: `0 0 6px ${threat.c}` }} />{threat.l}
        </span>
      </div>
    </div>
  );
}

export function HudTicker({ items }) {
  const list = items && items.length ? items : GENERIC_TICKER;
  const line = list.join("    \u25C8    ");
  return (
    <div data-testid="hud-ticker" className="relative overflow-hidden h-7 flex items-center border-b border-[color:var(--r-primary)]/12" style={{ background: "color-mix(in srgb, var(--r-void) 70%, transparent)" }}>
      <span className="shrink-0 px-2 h-full flex items-center font-mono text-[9px] tracking-[0.2em]" style={{ background: "var(--r-primary)", color: "#04131a" }}>● LIVE</span>
      <div className="hud-marquee whitespace-nowrap font-mono text-[11px]" style={{ color: "var(--r-text-muted)" }}>
        <span className="px-8">{line}</span><span className="px-8">{line}</span>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, pct, color = "var(--r-primary)" }) {
  return (
    <div className="rounded-lg p-3" style={{ border: "1px solid color-mix(in srgb, var(--r-primary) 18%, transparent)", background: "color-mix(in srgb, var(--r-surface) 80%, transparent)" }}>
      <div className="flex items-center gap-1.5 mb-1.5"><Icon size={12} style={{ color }} /><span className="font-mono tracking-[0.15em]" style={{ fontSize: 9, color: "var(--r-text-muted)" }}>{label}</span></div>
      <div className="font-mono text-lg" style={{ color: "var(--r-text)" }}>{value}</div>
      <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: "color-mix(in srgb, var(--r-text-muted) 18%, transparent)" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, boxShadow: `0 0 8px ${color}`, transition: "width .8s ease" }} />
      </div>
    </div>
  );
}

function Sparkline({ data }) {
  const w = 100, h = 34, max = 90;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * h}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: 56 }} data-testid="hud-sparkline">
      <polyline points={pts} fill="none" stroke="var(--r-primary)" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill="color-mix(in srgb, var(--r-primary) 12%, transparent)" stroke="none" />
    </svg>
  );
}

function AmbientLog({ extra = [] }) {
  const [lines, setLines] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    const pool = [...LOG_POOL, ...extra.filter(Boolean)];
    const p = (n) => String(n).padStart(2, "0");
    const gen = () => {
      const t = new Date();
      const ts = `${p(t.getHours())}:${p(t.getMinutes())}:${p(t.getSeconds())}`;
      setLines((prev) => [...prev.slice(-40), { id: t.getTime() + Math.random(), ts, msg: pool[Math.floor(Math.random() * pool.length)] }]);
    };
    for (let i = 0; i < 8; i++) gen();
    if (prefersReduced()) return;
    const id = setInterval(gen, 2200);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [lines]);
  return (
    <div ref={ref} data-testid="ambient-log" className="h-40 overflow-y-auto no-scrollbar font-mono text-[10px] leading-relaxed pr-2">
      {lines.map((l) => (
        <div key={l.id} style={{ color: "var(--r-text-muted)" }}>
          <span style={{ color: "color-mix(in srgb, var(--r-primary) 75%, transparent)" }}>{l.ts}</span>
          <span style={{ opacity: 0.4 }}> › </span>{l.msg}
        </div>
      ))}
    </div>
  );
}

export function NetworkMonitor({ hud, networkName }) {
  const m = useHudMetrics();
  const up = useUptime();
  const threat = THREAT[hud?.threat_level] || THREAT.NOMINAL;
  return (
    <div data-testid="network-monitor" className="h-full overflow-y-auto comms-bg view-in">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center gap-3 mb-1">
          <Activity size={16} style={{ color: "var(--r-primary)" }} />
          <span className="font-title font-black tracking-tight text-lg">MONITEUR DE MAILLAGE</span>
        </div>
        <div className="sys-label mb-8">{networkName} · ANALYSE DU SIGNAL EN CONTINU</div>

        <div className="flex flex-col items-center mb-10">
          <div className="radar"><span /><span /><span /><Radio size={24} style={{ color: "var(--r-primary)", opacity: 0.9 }} /></div>
          <div className="sys-label mt-5">AUCUNE TRANSMISSION SÉLECTIONNÉE</div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatTile icon={Gauge} label="INTÉGRITÉ" value={`${Math.round(m.integrity)}%`} pct={m.integrity} />
          <StatTile icon={Activity} label="LATENCE" value={`${Math.round(m.ping)}ms`} pct={100 - (m.ping / 96) * 100} color="var(--r-secondary)" />
          <StatTile icon={Server} label="RELAIS" value={`${m.relays}×`} pct={(m.relays / 8) * 100} />
          <StatTile icon={ShieldAlert} label="MENACE" value={threat.l} pct={hud?.threat_level === "CRITIQUE" ? 100 : hud?.threat_level === "ELEVE" ? 60 : 20} color={threat.c} />
        </div>

        <div className="rounded-lg p-4 mb-6" style={{ border: "1px solid color-mix(in srgb, var(--r-primary) 18%, transparent)", background: "color-mix(in srgb, var(--r-surface) 70%, transparent)" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="sys-label">ACTIVITÉ DU TRAFIC CHIFFRÉ</span>
            <span className="font-mono text-[10px]" style={{ color: "var(--r-primary)" }}>SYNC {m.sync.toFixed(1)}% · UP {up}</span>
          </div>
          <Sparkline data={m.activity} />
        </div>

        <div className="rounded-lg p-4" style={{ border: "1px solid color-mix(in srgb, var(--r-primary) 18%, transparent)", background: "color-mix(in srgb, var(--r-void) 55%, transparent)" }}>
          <div className="sys-label mb-2">JOURNAL SYSTÈME</div>
          <AmbientLog extra={hud?.ticker || []} />
        </div>
      </div>
    </div>
  );
}
